import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, IconButton, CircularProgress, Button } from '@mui/material';
import axios from 'axios';
import API_BASE from '../config';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import supabase from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const CommunityChat = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // {id,user_id,content,created_at}
  const [myName, setMyName] = useState('');
  const bottomRef = useRef(null);
  const inputBarRef = useRef(null);
  const [scrollPad, setScrollPad] = useState(0);
  const [bottomOffset, setBottomOffset] = useState(0);

  const EXTRA_BOTTOM_SPACE = 24;

  const formatTs = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString(undefined, {
      month: 'numeric', day: 'numeric', year: '2-digit',
      hour: 'numeric', minute: '2-digit',
    });
  };

  // Fetch recent messages and subscribe to realtime inserts
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) { setLoading(false); setMessages([]); return; }
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('community_messages')
          .select('id,user_id,content,created_at,author_name')
          .order('created_at', { ascending: true })
          .limit(200);
        if (error) throw error;
        if (!cancelled) setMessages(data || []);
      } catch (e) {
        console.error('Load community messages failed', e);
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Load my profile display name (if defined)
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();
        if (!cancelled) setMyName(data?.name || '');
      } catch (_) {}

      // Realtime subscription
      const channel = supabase.channel('realtime:community_messages')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'community_messages' }, (payload) => {
          const row = payload?.new;
          if (!row) return;
          setMessages((prev) => (prev.some(m => m.id === row.id) ? prev : [...prev, row]));
        })
        .subscribe();

      return () => {
        try { supabase.removeChannel(channel); } catch (_) {}
      };
    }
    const cleanup = load();
    return () => { if (typeof cleanup === 'function') cleanup(); cancelled = true; };
  }, [user]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const id = setTimeout(() => {
      if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 0);
    return () => clearTimeout(id);
  }, [messages, sending, scrollPad]);

  // Recompute scroll padding when input bar changes
  useEffect(() => {
    const pad = (inputBarRef.current?.offsetHeight || 0) + 16 + EXTRA_BOTTOM_SPACE;
    setScrollPad(pad);
  }, [bottomOffset, sending, input]);

  // Handle on-screen keyboard on mobile
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const THRESHOLD = 120;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const gap = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        setBottomOffset(gap > THRESHOLD ? gap : 0);
        const pad = (inputBarRef.current?.offsetHeight || 0) + 16 + EXTRA_BOTTOM_SPACE;
        setScrollPad(pad);
      });
    };
    update();
    vv.addEventListener('resize', update);
    return () => { cancelAnimationFrame(raf); vv.removeEventListener('resize', update); };
  }, []);

  const displayName = (m) => {
    const uid = m?.user_id;
    if (!uid) return 'Unknown';
    // Prefer stored author_name; for self, prefer current myName
    if (user && uid === user.id) return (myName?.trim() || m?.author_name?.trim() || 'You');
    return (m?.author_name?.trim() || `User ${String(uid).slice(0, 6)}`);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !user || sending) return;
    setSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || '';
      const resp = await axios.post(`${API_BASE}/community-message`, { content: text }, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });
      const row = resp?.data;
      if (row && row.id) {
        setMessages((prev) => (prev.some(m => m.id === row.id) ? prev : [...prev, row]));
      }
      setInput('');
    } catch (e) {
      console.error('Send failed', e);
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
        <Typography variant="h4" gutterBottom>Community Chat</Typography>
        <Typography sx={{ color: 'text.secondary', mb: 2 }}>
          Please sign in to chat with the community.
        </Typography>
        <Button variant="outlined" href="/auth">Sign in</Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, mt: 2, pb: { xs: 10, sm: 10 } }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Community Chat</Typography>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading…</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {messages.map((m) => {
            const mine = user && m.user_id === user.id;
            return (
              <Box key={m.id}>
                {/* Sender and timestamp */}
                <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {displayName(m)} • {formatTs(m.created_at)}
                  </Typography>
                </Box>
                {/* Bubble */}
                <Box sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                  <Box sx={{
                    bgcolor: mine ? '#A7CF83' : 'background.paper',
                    color: mine ? '#000000' : 'text.primary',
                    px: 1.5,
                    py: 1,
                    borderRadius: 5, // base radius for bottom + inner top corner
                    // Make the top OUTSIDE corner tighter
                    borderTopRightRadius: mine ? 2 :20,
                    borderTopLeftRadius:  mine ? 20 : 2,
                    maxWidth: '80%',
                    whiteSpace: 'pre-wrap',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}>
                    {m.content}
                  </Box>
                </Box>
              </Box>
            );
          })}
          <Box ref={bottomRef} sx={{ height: `${EXTRA_BOTTOM_SPACE}px`, scrollMarginBottom: `${scrollPad}px` }} />
        </Box>
      </Box>

      {/* Fixed bottom input bar */}
      <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: `${bottomOffset}px`, zIndex: 1100, borderTop: '0.25px solid #EAD7B2', bgcolor: 'background.paper', pb: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
        <Box ref={inputBarRef} sx={{ maxWidth: { xs: '100%', sm: 720 }, mx: 'auto', py: 1.5, px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Say something…"
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <IconButton onClick={handleSend} color="inherit" sx={{ bgcolor: '#A7CF83', color: '#000000', borderRadius: '50%', '&:hover': { bgcolor: '#97bf73' } }}>
            <ArrowUpwardIcon />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default CommunityChat;
